import { dev } from '$app/environment';
import { type Cache, InMemoryCache } from '@apollo/client/core/index.js';
import { relayStylePagination } from '@apollo/client/utilities/index.js';

export function createCache() {
  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          customers: relayStylePagination(['sort', 'nameFilter', 'productId']),
          games: relayStylePagination(['sort']),
          giftCards: relayStylePagination(['sort', 'states', 'as']),
          posts: relayStylePagination([
            'global',
            'following',
            'repliesOnly',
            'scheduledOnly',
            'accountId',
            'gameId',
            'sortBy',
            'sortWindow',
            'groupId',
            'groupChannelId',
            'includeGuideChapters'
          ]),
          announcements: relayStylePagination(),
          suggestedAccounts: relayStylePagination(['gameId']),
          coachings: relayStylePagination(['as', 'states', 'sort', 'gameId']),
          coachingSessions: relayStylePagination(['as', 'states', 'sort', 'coachingId']),
          groups: relayStylePagination(['gameId', 'gameSlug', 'sort', 'visibility']),
          chatRooms: relayStylePagination(),
          chatMessages: relayStylePagination(['accountId', 'roomId', 'sort', 'type']),
          products: relayStylePagination(['sort', 'productType']),
          orders: relayStylePagination(['sort', 'states']),
          subscriptions: relayStylePagination(['sort']),
          purchasedProducts: relayStylePagination(['sort', 'as', 'states']),
          coupons: relayStylePagination(['sort']),
          sales: relayStylePagination(['sort']),
          sessionAccounts: relayStylePagination(['sort']),
          testimonialRequests: relayStylePagination(),
          testimonials: relayStylePagination(['type', 'sort']),
          coachingPageAccounts: relayStylePagination(['priceValue', 'languages', 'queryType']),
          patreonGroups: relayStylePagination(),
          productGuides: relayStylePagination([
            'queryType',
            'priceValue',
            'languages',
            'chapterCount',
            'skipFollowedCheck',
            'sort'
          ]),
          productEvents: relayStylePagination(['sort', 'completed', 'languages']),
          myGuides: relayStylePagination(['states', 'sort', 'includeOwnGuides']),
          myEvents: relayStylePagination(['includeOwnEvents', 'sort', 'states']),
          myCoaches: relayStylePagination(),
          contestLeaderboard: relayStylePagination(['week']),
          purchasedPosts: relayStylePagination()
        }
      },
      // These are types without an id field
      API_Image: { keyFields: false, merge: true },
      API_ImageView: { keyFields: false, merge: true },
      API_ActiveSessionMetadata: { keyFields: false, merge: true },
      API_AccountDailyLimits: { keyFields: false, merge: true },
      API_AccountFeeCover: { keyFields: false, merge: true },
      API_AccountSocialAccount: { keyFields: false, merge: true },
      API_AccountMetricCount: { keyFields: false, merge: true },
      API_AccountMetricCountDailyPoint: { keyFields: false, merge: true },
      API_AccountMetricCustomer: { keyFields: false, merge: true },
      API_AccountMetricMoney: { keyFields: false, merge: true },
      API_AccountMetricMoneyDailyPoint: { keyFields: false, merge: true },
      API_AccountMetricReferralCount: { keyFields: false, merge: true },
      API_AccountSessionPriceRange: { keyFields: false, merge: true },
      API_AccountSettings: { keyFields: false, merge: true },
      API_AccountBookingNotice: { keyFields: false, merge: true },
      API_AccountAvailability: { keyFields: false, merge: true },
      API_AccountOverview: { keyFields: false, merge: true },
      API_AccountOverviewBillboard: { keyFields: false, merge: true },
      API_AccountOverviewSection: { keyFields: false, merge: true },
      API_AccountOverviewSections: { keyFields: false, merge: true },
      API_AuthenticationToken: { keyFields: false, merge: true },
      API_Availability: { keyFields: false, merge: true },
      API_BlockLayoutBlockCopyText: { keyFields: false, merge: true },
      API_BlockLayoutBlockGrid: { keyFields: false, merge: true },
      API_BlockLayoutBlockGroupSupporter: { keyFields: false, merge: true },
      API_BlockLayoutBlockGif: { keyFields: false, merge: true },
      API_BlockLayoutBlockImage: { keyFields: false, merge: true },
      API_BlockLayoutBlockPost: { keyFields: false, merge: true },
      API_BlockLayoutBlockLink: { keyFields: false, merge: true },
      API_BlockLayoutBlockProperties: { keyFields: false, merge: true },
      API_BlockLayoutBlockQuote: { keyFields: false, merge: true },
      API_BlockLayoutBlockQuoteBackground: { keyFields: false, merge: true },
      API_ContestLeaderboard: { keyFields: false, merge: true },
      API_DiscordGuildMember: { keyFields: false, merge: true },
      API_GameCommunityArtworkSocialAccounts: { keyFields: false, merge: true },
      API_GameFilter: { keyFields: false, merge: true },
      API_GameFilterCount: { keyFields: false, merge: true },
      API_GameFilterPriceRange: { keyFields: false, merge: true },
      API_GameFilterSession: { keyFields: false, merge: true },
      API_GameFilterGuide: { keyFields: false, merge: true },
      API_GameSeoData: { keyFields: false, merge: true },
      API_GameTitle: { keyFields: false, merge: true },
      API_GroupDiscount: { keyFields: false, merge: true },
      API_GroupMetricCount: { keyFields: false, merge: true },
      API_GroupMetricCountSimple: { keyFields: false, merge: true },
      API_GroupMetricMembers: { keyFields: false, merge: true },
      API_GroupMetricPosts: { keyFields: false, merge: true },
      API_GroupMetricRevenue: { keyFields: false, merge: true },
      API_SessionPriceRange: { keyFields: false, merge: true },
      API_Money: { keyFields: false, merge: true },
      API_OpenGraph: { keyFields: false, merge: true },
      API_PostEmbed: { keyFields: false, merge: true },
      API_PostReactionCount: { keyFields: false, merge: true },
      API_Purchase: { keyFields: false, merge: true },
      API_PurchaseReschedule: { keyFields: false, merge: true },
      API_RecentPostReaction: { keyFields: false, merge: true },
      API_PaymentMethod: { keyFields: false, merge: true },
      API_NotificationSettings: { keyFields: false, merge: true },
      API_RecoveryCodes: { keyFields: false, merge: true },
      API_OtpSetupCode: { keyFields: false, merge: true },
      // IRK API Types
      API_Account: {
        fields: {
          followers: relayStylePagination(['sort']),
          follows: relayStylePagination(['type', 'sort']),
          groups: relayStylePagination(['gameSlug', 'sort', 'nonOwnedOnly']),
          mutedAccounts: relayStylePagination(['sort']),
          notifications: relayStylePagination(),
          productCoachings: relayStylePagination(['sort', 'gameId', 'gameSlug', 'unlisted']),
          products: relayStylePagination(['type', 'sort']),
          productGuides: relayStylePagination(['states', 'sort']),
          productEvents: relayStylePagination(['states', 'sort', 'completed', 'replays']),
          walletTransactions: relayStylePagination(['sort']),
          testimonials: relayStylePagination(['type', 'sort', 'newOnly']),
          profileTestimonials: relayStylePagination(['type']),
          gamesOffered: relayStylePagination(),
          communities: relayStylePagination(['sort', 'type']),
          ownedGroups: relayStylePagination(['sort']),
          testimonialRequests: relayStylePagination(),
          pendingProductGuideAuthorRequests: relayStylePagination()
        }
      },
      API_AccountCustomer: {
        fields: {
          purchases: relayStylePagination(['sort'])
        }
      },
      API_AccountMetricMetafyPartner: {
        keyFields: false,
        merge: true,
        fields: {
          metafyPartnerReferrals: relayStylePagination(['sort'])
        }
      },
      API_AccountMetricReferral: {
        keyFields: false,
        merge: true,
        fields: {
          referrals: relayStylePagination(['sort'])
        }
      },
      API_ChatRoom: {
        fields: {
          messages: relayStylePagination(['sort', 'type'])
        }
      },
      API_Coupon: {
        fields: {
          limits: relayStylePagination()
        }
      },
      API_Sale: {
        fields: {
          limits: relayStylePagination()
        }
      },
      API_Game: {
        fields: {
          communityArtworks: relayStylePagination(['sort']),
          groups: relayStylePagination(['sort']),
          myGuides: relayStylePagination(),
          myCoaches: relayStylePagination(),
          productGuides: relayStylePagination(['sort', 'priceRange', 'languages', 'chapterCount']),
          productEvents: relayStylePagination(['sort', 'states', 'languages']),
          productCoachings: relayStylePagination(['sort']),
          accounts: relayStylePagination(['sort']),
          coachingPageAccounts: relayStylePagination(['sort', 'priceValue', 'languages', 'priceRange'])
        }
      },
      API_Group: {
        fields: {
          bans: relayStylePagination(['sort']),
          invites: relayStylePagination(['sort']),
          leaderboard: relayStylePagination(),
          members: relayStylePagination(['sort', 'roles', 'onlySupporters', 'groupTierId', 'nameFilter']),
          requests: relayStylePagination(['sort'])
        }
      },
      API_Post: {
        fields: {
          reactions: relayStylePagination(['sort', 'type']),
          replies: relayStylePagination(['sort']),
          unifiedTags: relayStylePagination(['sort'])
        }
      },
      API_Product: {
        fields: {
          accountsRecentlyPurchased: relayStylePagination(),
          groupTierBenefits: relayStylePagination(),
          unifiedTags: relayStylePagination()
        }
      },
      API_ProductCoaching: {
        fields: {
          sessionConnection: relayStylePagination()
        }
      },
      API_ProductGuide: {
        fields: {
          accountsRecentlyPurchased: relayStylePagination(),
          authors: relayStylePagination(),
          changelog: relayStylePagination(),
          chapters: relayStylePagination()
        }
      },
      API_ProductGuideChapter: {
        fields: {
          accountsOnChapter: relayStylePagination()
        }
      },
      API_PurchasedProduct: {
        fields: {
          giftCards: relayStylePagination(),
          orderItems: relayStylePagination(),
          orders: relayStylePagination(),
          policies: relayStylePagination()
        }
      },
      API_PurchasedProductCoaching: {
        fields: {
          sessionConnection: relayStylePagination(['states', 'sort'])
        }
      },
      API_UnifiedTag: {
        fields: {
          games: relayStylePagination(),
          products: relayStylePagination(['gameIds']),
          posts: relayStylePagination(['gameIds'])
        }
      }
    }
  });

  // Logging cache issues
  if (dev) {
    const originalRead = cache.read;
    cache.read = function <T>(options: Cache.ReadOptions) {
      const result = originalRead.call(this, options) as T | null;
      if (!result) {
        console.log(`[${new Date().toISOString()}] Cache read miss:`, {
          query: options.query?.definitions[0]?.name?.value,
          variables: options.variables,
          optimistic: options.optimistic
        });
      }
      return result;
    };

    // Log cache diff status
    const originalDiff = cache.diff;
    cache.diff = function <T>(options: Cache.DiffOptions) {
      const result = originalDiff.call(this, options) as Cache.DiffResult<T>;
      if (result.complete === false) {
        console.log(`[${new Date().toISOString()}] Cache diff incomplete:`, {
          id: options.id,
          query: options.query?.definitions[0]?.name?.value,
          variables: options.variables,
          missing: result.missing
        });
      }
      return result;
    };
  }
  return cache;
}
